import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { CalendarIcon, Send, MapPin, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { BRAND } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COURSES = [
  "Beginner Rock Climbing",
  "Group / Charity Abseil",
  "Private Single Pitch",
  "Not sure — please advise",
];

export default function BookingForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    course: "",
    group_size: 1,
    message: "",
  });
  const [date, setDate] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.course) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...form,
        group_size: Number(form.group_size) || 1,
        preferred_date: date ? format(date, "yyyy-MM-dd") : null,
      });
      toast.success("Enquiry sent — we'll be in touch within 24 hours.");
      setForm({
        name: "",
        email: "",
        phone: "",
        course: "",
        group_size: 1,
        message: "",
      });
      setDate(undefined);
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong sending your enquiry. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="book"
      data-testid="booking-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <div className="overline mb-4">Book / Enquire</div>
          <h2
            className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-[#2A2726] tracking-tight leading-[1.05]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Plan your day
            <br />
            <span className="italic font-extralight text-[#5C5654]">
              on the rock.
            </span>
          </h2>
          <p className="mt-6 text-[#5C5654] text-lg leading-relaxed max-w-md">
            Tell us a little about who's coming, what you'd like to do and a
            rough date. We'll come back within 24 hours with availability and a
            recommended venue.
          </p>

          <div className="mt-10 space-y-5">
            <div className="flex items-center gap-3 text-[#2A2726]">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: "rgba(63,182,178,0.12)" }}
              >
                <Mail size={16} style={{ color: "#3FB6B2" }} />
              </div>
              <a href={`mailto:${BRAND.email}`} className="hover:text-[#3FB6B2]">
                {BRAND.email}
              </a>
            </div>
            <div className="flex items-center gap-3 text-[#2A2726]">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: "rgba(63,182,178,0.12)" }}
              >
                <Phone size={16} style={{ color: "#3FB6B2" }} />
              </div>
              <a href={`tel:${BRAND.phone}`} className="hover:text-[#3FB6B2]">
                {BRAND.phone}
              </a>
            </div>
            <div className="flex items-center gap-3 text-[#2A2726]">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: "rgba(63,182,178,0.12)" }}
              >
                <MapPin size={16} style={{ color: "#3FB6B2" }} />
              </div>
              <span>Lancashire · Lake District National Park</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="booking-form"
            noValidate
            className="bg-[#F9F8F6] rounded-2xl p-8 md:p-12 border border-black/5"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
              <Field label="Your name *">
                <input
                  data-testid="booking-name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className="form-line"
                  placeholder="Jane Doe"
                />
              </Field>

              <Field label="Email *">
                <input
                  data-testid="booking-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="form-line"
                  placeholder="you@example.com"
                />
              </Field>

              <Field label="Phone *">
                <input
                  data-testid="booking-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="form-line"
                  placeholder="07000 000 000"
                />
              </Field>

              <Field label="Group size">
                <input
                  data-testid="booking-group-size"
                  type="number"
                  min={1}
                  max={50}
                  value={form.group_size}
                  onChange={(e) => update("group_size", e.target.value)}
                  className="form-line"
                />
              </Field>

              <Field label="Course / service *" className="md:col-span-2">
                <Select
                  value={form.course}
                  onValueChange={(v) => update("course", v)}
                >
                  <SelectTrigger
                    data-testid="booking-course-trigger"
                    className="form-line h-auto justify-between [&>span]:text-left"
                  >
                    <SelectValue placeholder="Choose a course…" />
                  </SelectTrigger>
                  <SelectContent data-testid="booking-course-options">
                    {COURSES.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`course-${c}`}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Preferred date" className="md:col-span-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="booking-date-trigger"
                      className="form-line text-left flex items-center justify-between"
                    >
                      <span className={date ? "text-[#2A2726]" : "text-[#5C5654]/50"}>
                        {date ? format(date, "EEEE, d MMMM yyyy") : "Pick a date…"}
                      </span>
                      <CalendarIcon size={16} className="text-[#5C5654]" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    className="w-auto p-0"
                    data-testid="booking-date-popover"
                  >
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </Field>

              <Field label="Tell us more" className="md:col-span-2">
                <textarea
                  data-testid="booking-message"
                  rows={4}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  className="form-line resize-none"
                  placeholder="Goals for the day, charity name, group ages, anything we should know…"
                />
              </Field>
            </div>

            <button
              type="submit"
              data-testid="booking-submit"
              disabled={submitting}
              className="mt-10 btn-primary w-full md:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Sending…" : "Send enquiry"} <Send size={16} />
            </button>

            <p className="mt-5 text-xs text-[#5C5654]/70 leading-relaxed">
              By sending this form you agree to be contacted about your enquiry.
              We'll never share your details.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <Label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5654]/80 font-semibold">
        {label}
      </Label>
      {children}
    </div>
  );
}
